import React, { useState } from "react";
import { Image as ImageIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EditIcon, Delete02Icon, PlusIcon } from "../../../../../assets/icons";
import CompanyProductModal from "../../../../../components/modals/company/CompanyProductModal";
import DeletePopup from "../../../../../components/delete-popup";
import {
  createCompanyProduct,
  updateCompanyProduct,
  deleteCompanyProduct,
} from "../../../../../redux/slice/companySlice";
import PreviewModal from "../../../../../components/modals/previewModal";
import { usePermission } from "../../../../../hooks/usePermission";
import { useToast } from "../../../../../context/TostContext";

const MODULE = "company_management";

const ImageSwiper = ({ media, title, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!media || media.length === 0) return null;

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const currentMedia = media[currentIndex];

  return (
    <div
      className="position-relative rounded-3 overflow-hidden border shadow-sm"
      style={{ width: "80px", height: "80px", flexShrink: 0, backgroundColor: "#f8f9fa" }}
    >
       {currentMedia?.mimetype === "video/mp4" ?
                <video src={currentMedia.file?.url || currentMedia.url}
                    alt={title || "Product Media"}
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }} /> :
                <img
        src={currentMedia.file?.url || currentMedia.url}
        alt={title || "Product Media"}
        className="w-100 h-100"
        style={{ objectFit: "cover", cursor: "pointer" }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onImageClick) onImageClick(currentIndex);
        }}
        onError={(e) => {
          e.target.src = "/images/image-placeholder.png";
        }}
      />}
      
      {media.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="position-absolute top-50 start-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
            style={{ width: "16px", height: "16px", borderRadius: "0 4px 4px 0", cursor: "pointer", fontSize: "10px" }}
          >
            &#10094;
          </button>
          <button
            onClick={handleNext}
            className="position-absolute top-50 end-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
            style={{ width: "16px", height: "16px", borderRadius: "4px 0 0 4px", cursor: "pointer", fontSize: "10px" }}
          >
            &#10095;
          </button>
          <div className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-1 mb-1">
            {media.map((_, idx) => (
              <div
                key={idx}
                className="rounded-circle"
                style={{
                  width: "3px",
                  height: "3px",
                  backgroundColor: currentIndex === idx ? "white" : "rgba(255,255,255,0.5)",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Products = () => {
  const {id} = useParams();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const toast=useToast();
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMediaArray, setPreviewMediaArray] = useState([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);
  const [expandedProducts, setExpandedProducts] = useState({});

  const { companyDetail, productActionLoading } = useSelector((state) => state.company);

  const toggleExpand = (id) => {
    setExpandedProducts((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleUpsertProduct = (data) => {
    const action = currentItem
      ? updateCompanyProduct({ companyId: id, id: currentItem.id, data })
      : createCompanyProduct({ companyId: id, data });

    dispatch(action)
      .unwrap()
      .then(() => {
        toast.success(`Product ${currentItem ? "updated" : "added"} successfully`);
        setShowModal(false);
        setCurrentItem(null);
      })
      .catch((error) => {
        toast.error(error?.message || `Failed to ${currentItem ? "update" : "add"} product`);
      });
  };

  const handleDeleteProduct = () => {
    dispatch(deleteCompanyProduct({ companyId: id, id: itemToDelete }))
      .unwrap()
      .then(() => {
        toast.success("Product deleted successfully");
        setShowDeletePopup(false);
        setItemToDelete(null);
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to delete product");
      });
  };

  return (
    <div className="position-relative h-100">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="fw-bold mb-0" style={{ fontSize: "16px", color: "#444050" }}>
          Products
        </h5>
        {hasPermission(MODULE, "update") && (
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => {
              setCurrentItem(null);
              setShowModal(true);
            }}
          >
            <PlusIcon size={14} />
            Add Product
          </button>
        )}
      </div>

      {companyDetail?.products?.length > 0 ? (
        <div className="row g-3">
          {companyDetail.products.map((prod, index) => (
            <div key={index} className="col-12 col-md-6">
              <div className="p-3 border rounded-3 bg-light-subtle position-relative custom-card h-100">
                <div className="position-absolute top-0 end-0 p-2 d-flex gap-2">
                  {hasPermission(MODULE, "update") && (
                    <button
                      className="post-action-btn edit-btn border rounded p-1 bg-white"
                      onClick={() => {
                        setCurrentItem(prod);
                        setShowModal(true);
                      }}
                    >
                      <EditIcon width={12} height={12} color="#007bff" />
                    </button>
                  )}
                  {hasPermission(MODULE, "delete") && (
                    <button
                      className="post-action-btn delete-btn border rounded p-1 bg-white"
                      onClick={() => {
                        setItemToDelete(prod.id);
                        setShowDeletePopup(true);
                      }}
                    >
                      <Delete02Icon width={12} height={12} color="#FF0000" />
                    </button>
                  )}
                </div>
                <div className="d-flex gap-3 align-items-start">
                  {prod.media?.length > 0 ? (
                    <ImageSwiper
                      media={prod.media}
                      title={prod.title}
                      onImageClick={(idx) => {
                        const mediaArray = prod.media.map((item) => ({
                          url: item.file?.url || item.url,
                          title: prod.title || "Product Image",
                          type: "image",
                        }));
                        setPreviewMediaArray(mediaArray);
                        setPreviewInitialIndex(idx);
                        setShowPreviewModal(true);
                      }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center border rounded-3"
                      style={{ width: "80px", height: "80px", flexShrink: 0, backgroundColor: "#f8f9fa" }}
                    >
                      <ImageIcon size={24} color="#adb5bd" />
                    </div>
                  )}
                  <div className="flex-grow-1 overflow-hidden" style={{ paddingRight: "75px" }}>
                    <h6 className="fw-semibold text-dark mb-1 text-break" style={{ fontSize: "14px" }}>
                      {prod.title || "-"}
                    </h6>
                    {prod.description && (
                      <p className="text-secondary text-break mb-0 mt-2 small" style={{ lineHeight: "1.5" }}>
                        {prod.description.length > 150 && !expandedProducts[prod.id] ? (
                          <>
                            {prod.description.slice(0, 150)}...{" "}
                            <span
                              className="text-primary fw-semibold ms-1"
                              onClick={() => toggleExpand(prod.id)}
                              style={{ cursor: "pointer" }}
                            >
                              Show more
                            </span>
                          </>
                        ) : prod.description.length > 150 ? (
                          <>
                            {prod.description}{" "}
                            <span
                              className="text-primary fw-semibold ms-1"
                              onClick={() => toggleExpand(prod.id)}
                              style={{ cursor: "pointer" }}
                            >
                              Show less
                            </span>
                          </>
                        ) : (
                          prod.description
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted text-center py-4 small">No products listed.</p>
      )}

      <CompanyProductModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setCurrentItem(null);
        }}
        onSubmit={handleUpsertProduct}
        product={currentItem}
        isLoading={productActionLoading}
        title={currentItem ? "Edit Company Product" : "Add Company Product"}
        size="lg"
      />

      <DeletePopup
        show={showDeletePopup}
        onHide={() => {
          setShowDeletePopup(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteProduct}
        content="Are you sure you want to delete this product? This action cannot be undone."
        saveTitle="Delete"
        disabled={productActionLoading}
      />

      <PreviewModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        previewMedia={previewMediaArray}
        initialIndex={previewInitialIndex}
      />
    </div>
  );
};

export default Products;
